/**
 * Trajectory feed health — is the publisher's schedule mirror stale?
 *
 * The nexsoss-rmf trajectory publisher marks a snapshot `stale: true`
 * (with `mirror_age_ms`) when its schedule mirror has stopped
 * updating, i.e. when the `trajectories` it would send describe an
 * empty or old mirror rather than the robots' real itineraries.
 * `useLiveData` records that per map under `trajectoryFeedKey(site)`
 * and keeps the last non-stale snapshot in the trajectories cache, so
 * the map freezes honestly instead of blanking — and this hook is how
 * the chrome says so (the chip next to the top-bar connection dot).
 *
 * Site-wide, not per level: the publisher has one mirror for every
 * map, so when it is stale every level's snapshot is stale together.
 * Older publishers never send the field, so this simply stays `false`
 * for them.
 */
import { useMemo } from 'react';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { trajectoryFeedKey } from '../scope';

export interface TrajectoryFeedMapStatus {
  stale: boolean;
  /** Age of the publisher's mirror when the snapshot was built, ms;
   *  null when the publisher does not report it. */
  mirrorAgeMs: number | null;
  /** SPA wall-clock time the status was recorded. */
  receivedAt: number;
}

/** Keyed by map (level) name. */
export type TrajectoryFeedStatus = Readonly<Record<string, TrajectoryFeedMapStatus>>;

export interface TrajectoryFeedStale {
  /** True when any map's latest snapshot carried `stale: true`. */
  stale: boolean;
  staleMaps: readonly string[];
  /** Largest reported mirror age across the stale maps, ms. */
  mirrorAgeMs: number | null;
}

const NOT_STALE: TrajectoryFeedStale = { stale: false, staleMaps: [], mirrorAgeMs: null };

export function useTrajectoryFeedStale(): TrajectoryFeedStale {
  const siteId = useSafeSiteId();
  const { data } = useQuery<TrajectoryFeedStatus>({
    queryKey: trajectoryFeedKey(siteId),
    // Fed by the stream only (see useTrajectories for why skipToken).
    queryFn: skipToken,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return useMemo(() => {
    if (!data) return NOT_STALE;
    const staleMaps = Object.keys(data).filter((map) => data[map]?.stale === true);
    if (staleMaps.length === 0) return NOT_STALE;
    let mirrorAgeMs: number | null = null;
    for (const map of staleMaps) {
      const age = data[map]?.mirrorAgeMs ?? null;
      if (age !== null && (mirrorAgeMs === null || age > mirrorAgeMs)) mirrorAgeMs = age;
    }
    return { stale: true, staleMaps, mirrorAgeMs };
  }, [data]);
}
